import { useState, useRef, useEffect } from 'preact/hooks'
import { Controller, type UseFormWatch, type UseFormSetValue, type Control } from 'react-hook-form'
import type { OnlineEventAIForm } from '@/hooks/useOnlineEventForm'
import { MarkdownView } from '@/components/common/MarkdownView'
import { Pencil, Save, X, FileText } from 'lucide-preact'
import { websApi, type WebViewDto, type OnlineEventDetailsPatchDto } from '@/api/webs'
import { useToast } from '@/hooks/useToast'
import { useWorkspaceCapabilities } from '@/hooks/useWorkspaceCapabilities'
import { RichTextBox } from '@/components/common/RichTextBox'

interface OverviewCardProps {
  onlineEvent: WebViewDto['OnlineEvent']
  web: WebViewDto
  editingOverview: boolean
  savingOverview: boolean
  editDisabled: boolean
  onEditStart: () => void
  onEditEnd: () => void
  onSavingChange: (v: boolean) => void
  onPatchWeb: (patcher: (prev: WebViewDto) => WebViewDto) => void
  watch: UseFormWatch<OnlineEventAIForm>
  setValue: UseFormSetValue<OnlineEventAIForm>
  control: Control<OnlineEventAIForm>
}

export function OverviewCard({
  onlineEvent, web, editingOverview, savingOverview, editDisabled,
  onEditStart, onEditEnd, onSavingChange, onPatchWeb,
  watch, setValue, control,
}: OverviewCardProps) {
  const { showSuccess, showError } = useToast()
  const { canEdit } = useWorkspaceCapabilities()
  const [overviewExpanded, setOverviewExpanded] = useState(false)
  const [overviewWasClamped, setOverviewWasClamped] = useState(false)
  const overviewParaRef = useRef<HTMLDivElement | null>(null)
  const overviewCardRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (editingOverview) return
    const el = overviewParaRef.current
    if (el && !overviewExpanded) {
      setOverviewWasClamped(el.scrollHeight > el.clientHeight + 1)
    }
  }, [onlineEvent?.Overview, overviewExpanded, editingOverview])

  const saveOverview = async () => {
    onSavingChange(true)
    const overviewValue = watch('overview').trim()
    const isOnDemand = onlineEvent?.ModeType?.toLowerCase() === 'on-demand (recording only)'
    const dto: OnlineEventDetailsPatchDto = {
      ETag: web.ETag,
      EventType: onlineEvent?.EventType || '',
      ModeType: onlineEvent?.ModeType || '',
      Title: onlineEvent?.Title || '',
      TimeZone: isOnDemand ? undefined : onlineEvent?.TimeZone,
      StartDateTime: isOnDemand ? undefined : onlineEvent?.StartDateTime,
      EndDateTime: isOnDemand ? undefined : onlineEvent?.EndDateTime,
      ConferenceId: (onlineEvent?.ConferenceId || '').trim() ? onlineEvent?.ConferenceId : undefined,
      Overview: overviewValue || undefined,
    }
    const res = await websApi.updateOnlineEventDetails(web.WorkspaceId, web.Id, dto)
    if (!res.error && res.data) {
      onPatchWeb(() => res.data!)
      showSuccess('Overview updated', 'Overview saved.')
      onEditEnd()
    } else if (res.status !== 422) {
      showError('Save failed', res.error || 'Unable to update overview')
    }
    onSavingChange(false)
  }

  const cancelOverviewEdit = () => {
    onEditEnd()
    setValue('overview', onlineEvent?.Overview || '')
  }

  const overviewText = (onlineEvent?.Overview || '').trim()

  return (
    <div ref={overviewCardRef} className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5 shadow-sm">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-900 dark:text-white flex items-center gap-2"><FileText className="w-5 h-5 text-primary-600" /> Overview</h2>
        {!editingOverview && canEdit && (
          <div className="flex items-center gap-3">
            <button
              type="button"
              disabled={editDisabled}
              onClick={() => { onEditStart(); setValue('overview', onlineEvent?.Overview || '') }}
              className="inline-flex items-center gap-2 px-2.5 py-1.5 text-xs rounded-md border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Pencil className="w-3 h-3" /> {overviewText ? 'Edit' : 'Add'}
            </button>
          </div>
        )}
      </div>
      {!editingOverview ? (
        overviewText.length === 0
          ? <p className="mt-3 text-sm italic text-slate-400 dark:text-slate-500">No overview added.</p>
          : <MarkdownView ref={overviewParaRef} className={`mt-3 text-sm text-slate-700 dark:text-slate-300 ${!overviewExpanded ? 'line-clamp-5' : ''}`}>{overviewText}</MarkdownView>
      ) : (
        <form className="mt-3 space-y-3" onSubmit={(e) => { e.preventDefault(); saveOverview() }}>
          <Controller
            name="overview"
            control={control}
            render={({ field }) => (
              <RichTextBox
                name="overview"
                label="Overview"
                labelSize="xs"
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                placeholder="Add a brief overview"
                minHeight="120px"
              />
            )}
          />
          <div className="flex items-center gap-2">
            <button type="button" onClick={saveOverview} disabled={savingOverview} className="inline-flex items-center gap-2 px-3 py-1.5 text-xs rounded-md bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"><Save className="w-3 h-3" /> {savingOverview ? 'Saving...' : 'Save'}</button>
            <button type="button" onClick={cancelOverviewEdit} disabled={savingOverview} className="inline-flex items-center gap-2 px-3 py-1.5 text-xs rounded-md border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700"><X className="w-3 h-3" /> Cancel</button>
          </div>
        </form>
      )}
      {!editingOverview && (overviewWasClamped || overviewExpanded) && (
        <div className="mt-4 flex justify-center">
          <button
            type="button"
            onClick={() => {
              setOverviewExpanded(prev => {
                const next = !prev
                if (!next) overviewCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                return next
              })
            }}
            className="text-xs font-medium text-primary-600 dark:text-primary-400 hover:underline px-3 py-1 rounded"
          >
            {overviewExpanded ? 'Show Less' : 'Read More'}
          </button>
        </div>
      )}
    </div>
  )
}
